type MonthKey = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type YearKey = string;

type EnergyType = 'electricity' | 'districtHeating' | 'fuel' | 'unknown';

type UiCategorized = {
  typeOfEnergy: EnergyType;
  avg: number;
  total: number;
  kWp: number;
  totalCo2: number;
  breakdown: Array<{
    deviceTag: string;
    total: number;
    kWp: number;
    totalCo2: number;
  }>;
};

type NaiveBaselinePreview = {
  averagesPerMonth: Array<{
    year: number;
    months: Array<{
      month: number;
      categorized: UiCategorized[];
    }>;
  }>;
};

export type BaselineChartData = {
  monthlyAverages: Record<string, number>; // baseline line (kWh) = mean across years (excluding excluded months)
  monthlyYearlyAverages: Record<string, Record<YearKey, number | null>>; // bars (kWh per year)
  monthlyMaxAvg: Record<string, number>; // max kWp line = mean across years (excluding excluded months)
};

const monthKey = (year: number, month: number) => `${year}-${month}`;

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function mean(values: Array<number | null | undefined>): number | null {
  const nums = values.filter(isFiniteNumber);
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function clampMonth(m: number): MonthKey | null {
  if (!Number.isFinite(m)) return null;
  const mm = Math.trunc(m);
  if (mm < 1 || mm > 12) return null;
  return mm as MonthKey;
}

function sumTotals(categorized: UiCategorized[] | undefined): number {
  if (!categorized?.length) return 0;
  return categorized.reduce((acc, c) => acc + Number(c.total ?? 0), 0);
}

function maxKWp(categorized: UiCategorized[] | undefined): number {
  if (!categorized?.length) return 0;
  let max = 0;
  for (const c of categorized) {
    const v = Number(c.kWp ?? 0);
    if (v > max) max = v;
  }
  return max;
}

export function buildBaselineChartDataFromPreview(opts: {
  preview: NaiveBaselinePreview;
  excluded: Set<string>;
}): { years: string[]; chart: BaselineChartData } {
  const { preview, excluded } = opts;

  const years = Array.from(
    new Set((preview.averagesPerMonth ?? []).map((y) => String(y.year)))
  ).sort();

  const monthlyYearlyAverages: BaselineChartData['monthlyYearlyAverages'] = {};
  const monthlyAverages: BaselineChartData['monthlyAverages'] = {};
  const monthlyMaxAvg: BaselineChartData['monthlyMaxAvg'] = {};

  for (let m = 1; m <= 12; m++) {
    const mk = String(m);
    monthlyYearlyAverages[mk] = Object.fromEntries(years.map((y) => [y, null]));
    monthlyAverages[mk] = 0;
    monthlyMaxAvg[mk] = 0;
  }

  // Fill year/month totals (bars)
  for (const y of preview.averagesPerMonth ?? []) {
    const yearStr = String(y.year);

    for (const m of y.months ?? []) {
      const month = clampMonth(m.month);
      if (!month) continue;

      const mk = String(month);

      if (excluded.has(monthKey(y.year, month))) {
        monthlyYearlyAverages[mk][yearStr] = null;
        continue;
      }

      monthlyYearlyAverages[mk][yearStr] = sumTotals(m.categorized);
    }
  }

  for (let m = 1; m <= 12; m++) {
    const mk = String(m);

    const baselineMean = mean(Object.values(monthlyYearlyAverages[mk]));
    monthlyAverages[mk] = baselineMean ?? 0;

    const kwpPerYear: Array<number | null> = [];
    for (const y of preview.averagesPerMonth ?? []) {
      if (excluded.has(monthKey(y.year, m))) {
        kwpPerYear.push(null);
        continue;
      }

      const monthObj = (y.months ?? []).find((mm) => mm.month === m);
      if (!monthObj) {
        kwpPerYear.push(null);
        continue;
      }

      kwpPerYear.push(maxKWp(monthObj.categorized));
    }

    const kwpMean = mean(kwpPerYear);
    monthlyMaxAvg[mk] = kwpMean ?? 0;
  }

  return {
    years,
    chart: { monthlyAverages, monthlyYearlyAverages, monthlyMaxAvg },
  };
}
